import { Sun, Moon, Monitor } from './icons';
import { useTheme, type ThemeMode } from '../contexts/ThemeContext';

const META: Record<ThemeMode, { label: string; Icon: typeof Sun }> = {
  system: { label: '跟随系统', Icon: Monitor },
  light: { label: '浅色', Icon: Sun },
  dark: { label: '深色', Icon: Moon },
};

/**
 * 桌面账户胶囊内：月亮|太阳两段「常显」切换器。
 * 高亮跟随当前生效主题(resolved)；点月亮=深色、点太阳=浅色（显式固定）。
 * 不含「跟随系统」档——首访仍默认 system，直到点击其一；系统档保留在 ThemeToggleSegment。
 */
export function ThemeToggleSwitch() {
  const { resolved, setMode } = useTheme();
  const seg = 'flex h-7 w-7 items-center justify-center rounded-full transition-colors';
  const active = 'bg-surface text-navy shadow-sm';
  const idle = 'text-fg-subtle hover:text-fg';
  return (
    <div
      role="group"
      aria-label="日夜主题切换"
      className="flex items-center gap-0.5 rounded-full border border-line bg-surface-muted p-0.5"
    >
      <button
        type="button"
        onClick={() => setMode('dark')}
        aria-pressed={resolved === 'dark'}
        aria-label="深色主题"
        title="深色"
        className={`${seg} ${resolved === 'dark' ? active : idle}`}
      >
        <Moon className="h-3.5 w-3.5" />
      </button>
      <button
        type="button"
        onClick={() => setMode('light')}
        aria-pressed={resolved === 'light'}
        aria-label="浅色主题"
        title="浅色"
        className={`${seg} ${resolved === 'light' ? active : idle}`}
      >
        <Sun className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

/** 移动端（展开菜单内）：3 选一分段控件，明确列出 系统 / 浅色 / 深色。 */
export function ThemeToggleSegment() {
  const { mode, setMode } = useTheme();
  const order: ThemeMode[] = ['system', 'light', 'dark'];
  return (
    <div className="mx-3 my-1">
      <p className="mb-1 text-xs text-fg-subtle">主题</p>
      <div className="grid grid-cols-3 gap-1 rounded-card border border-line p-1">
        {order.map((key) => {
          const { label, Icon } = META[key];
          const active = mode === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => setMode(key)}
              aria-pressed={active}
              className={`flex items-center justify-center gap-1 py-1.5 text-xs transition-colors ${
                active
                  ? 'bg-navy-fill text-white'
                  : 'text-fg-muted hover:bg-surface-muted hover:text-fg'
              }`}
            >
              <Icon className="h-3.5 w-3.5" />
              {key === 'system' ? '系统' : label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
